import React, { useEffect } from "react";
import { useParams } from "react-router-dom";

import { useStatus } from "@/api/statuses";
import MissingIndicator from "@/components/missing-indicator";
import SiteLogo from "@/components/site-logo";
import Status from "@/components/status";
import { Spinner } from "@/components/ui";
import { iframeId } from "@/iframe";

/** Status to be presented in an iframe for embeds on external websites. */
const EmbeddedStatus: React.FC = () => {
  // Intercept same-tab link navigation inside the embed and open externally.
  useEffect(() => {
    const handleCapturedClick = (event: MouseEvent) => {
      if (event.defaultPrevented) return;

      const target = event.target as HTMLElement | null;
      const anchor = target?.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;

      // Allow already externalized links.
      if (anchor.target === "_blank") return;

      event.preventDefault();
      event.stopPropagation();

      if (anchor.href) {
        window.open(anchor.href, "_blank", "noopener,noreferrer");
      }
    };

    document.addEventListener("click", handleCapturedClick, true);
    return () => {
      document.removeEventListener("click", handleCapturedClick, true);
    };
  }, []);

  const { statusId } = useParams<{ statusId: string }>();

  const { status, isLoading } = useStatus(statusId ?? null);

  /** For statuses with images. Value derived from:
      padding = 48px
      header = 42.5px
      action bar = 48px **/
  const extraPadding = 138.5;

  const setIframeHeight = (height: number) => {
    window.parent.postMessage(
      {
        type: "setHeight",
        id: iframeId,
        height: height,
      },
      "*",
    );
  };

  useEffect(() => {
    setIframeHeight(document.getElementsByTagName("html")[0].scrollHeight);

    const image: HTMLImageElement | null = document.querySelector(
      "#media-gallery-image-thumbnail img",
    );

    if (image) {
      image.onload = function () {
        setIframeHeight(image.clientHeight + extraPadding);
      };
    }
  }, [status, isLoading]);

  const logo = (
    <div className="ml-4 flex justify-center align-middle">
      <SiteLogo className="max-h-[20px] max-w-[112px]" />
    </div>
  );

  const renderInner = () => {
    if (isLoading) {
      return (
        <div className="mt-4">
          <Spinner />
        </div>
      );
    } else if (status) {
      return <Status status={status} accountAction={logo} variant="default" />;
    } else {
      return <MissingIndicator />;
    }
  };

  return (
    <a
      className="block bg-white dark:bg-gray-900"
      href={status?.url || "#"}
      onClick={(e) => e.stopPropagation()}
      target="_blank"
    >
      <div className="pointer-events-none max-w-3xl">{renderInner()}</div>
    </a>
  );
};

export default EmbeddedStatus;
